// Single source of truth for credit-card pricing conversion.
// Cash is the authored price; every CC plan = cash ÷ factor, rounded UP to RM10.
// Used by the create/edit Price Reference, Section 2, and the frozen quotation
// snapshot so all three tally (latest rates 2026-06-17).
export const CC_FACTORS = { 12: 0.95, 24: 0.93, 36: 0.915, 60: 0.88 };

export const roundUp10 = (v) => Math.ceil((Number(v) || 0) / 10) * 10;

// cash → credit-card price for a term (12/24/36/60). 0 in → 0 out.
export const ccFromCash = (cash, term) => {
    const c = Number(cash) || 0;
    if (c <= 0) return 0;
    return roundUp10(c / (CC_FACTORS[term] || CC_FACTORS[36]));
};

// Payment-method string → conversion factor (1 = cash, no conversion).
export const factorForMethod = (pm) => {
    const s = String(pm || '').toLowerCase();
    return s.includes('60') ? 0.88 : s.includes('36') ? 0.915 : s.includes('24') ? 0.93 : s.includes('12') ? 0.95 : 1;
};

// Payment-method string → term in months (0 = cash).
export const termForMethod = (pm) => {
    const s = String(pm || '').toLowerCase();
    return s.includes('60') ? 60 : s.includes('36') ? 36 : s.includes('24') ? 24 : s.includes('12') ? 12 : 0;
};

// THE single canonical Payment Method option list — used IDENTICALLY by the
// create-sales form AND the RSC calculator so the RSC → Create Sales hand-off is a
// direct value copy (no lossy remapping). Keep both dropdowns wired to this.
export const PAYMENT_METHOD_OPTIONS = [
    { label: 'CASH', value: 'CASH' },
    { label: 'CREDIT CARD 12 MONTHS', value: 'creditCard12' },
    { label: 'CREDIT CARD 24 MONTHS', value: 'creditCard24' },
    { label: 'CREDIT CARD 36 MONTHS', value: 'creditCard36' },
    { label: 'CREDIT CARD 60 MONTHS', value: 'creditCard60' },
    { label: 'MIX (CASH + CC)', value: 'mix' },
];

// Human label for the selected payment method (printed on the quotation).
export const paymentLabel = (pm) => {
    const s = String(pm || '').toLowerCase();
    if (s.includes('mix')) return 'Mix (Cash + CC)';
    const term = termForMethod(pm);
    return term > 0 ? `Credit Card (${term} Months)` : 'Cash';
};

// Convert a CASH pricing breakdown to a payment-method basis. This is THE single
// rule used by the create/edit Section-2 panel, the frozen quotation snapshot, and
// the commission. Rounding matches the RSC calculator (verified 2026-06-18):
//   • System (Original/Astern → Final) uses the CC PRICE LIST: Original = roundUp10
//     (cash ÷ factor), discounts = round(cash ÷ factor). This is the COMMISSION basis.
//   • TOTAL is rounded UP ONCE on the whole cash final (system − discounts + add-ons),
//     exactly like RSC's ccFromCash — NOT per component. The add-on is the RESIDUAL
//     (total − final) so the breakdown rows still add up to the total.
// term: 12/24/36/60 (credit card) or 0/falsy (cash, no conversion).
export function methodBreakdown({ originalPrice = 0, asternDiscount = 0, agentDiscount = 0, addOnPrice = 0 }, term) {
    const n = (v) => Number(v) || 0;
    const factor = CC_FACTORS[term];
    if (!term || !factor) {
        const final = Math.max(0, n(originalPrice) - n(asternDiscount) - n(agentDiscount));
        return { original: n(originalPrice), astern: n(asternDiscount), agent: n(agentDiscount), addon: n(addOnPrice), final, total: final + n(addOnPrice) };
    }
    const original = roundUp10(n(originalPrice) / factor);     // CC price-list system price
    const astern = Math.round(n(asternDiscount) / factor);
    const agent = Math.round(n(agentDiscount) / factor);
    const final = Math.max(0, original - astern - agent);      // system final = commission basis
    // RSC parity: round up the COMBINED cash final once.
    const cashTotal = Math.max(0, n(originalPrice) - n(asternDiscount) - n(agentDiscount)) + n(addOnPrice);
    const total = roundUp10(cashTotal / factor);
    const addon = Math.max(0, total - final);                  // residual → rows sum to total
    return { original, astern, agent, addon, final, total };
}
